import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, Button, Collapse, IconButton } from '@mui/material';
import { SearchRounded, CloseRounded, ArrowForwardRounded } from '@mui/icons-material';
import { CATEGORIES, TEMPLATES } from './templates';
import TemplatePreview from './TemplatePreview';
import { BRAND } from '../theme';

const TemplateCard = ({ template }) => (
  <Box component={RouterLink} to={`/templates/${template.slug}`} sx={{
    display: 'block', textDecoration: 'none', color: 'inherit',
    '&:hover .tpl-frame': { transform: 'translateY(-6px)', boxShadow: '0 22px 48px rgba(27,29,43,0.16)' },
    '&:hover .tpl-overlay': { opacity: 1 },
    '&:hover .template-preview-site': { '--tpl-scroll': '-55%' },
  }}>
    <Box className="tpl-frame" sx={{
      position: 'relative', borderRadius: '14px', overflow: 'hidden', bgcolor: '#fff',
      boxShadow: BRAND.softShadow, transition: 'transform .3s ease, box-shadow .3s ease',
    }}>
      {/* Browser chrome */}
      <Box sx={{ display: 'flex', gap: 0.6, px: 1.5, py: 1, bgcolor: '#f1f2f6' }}>
        {['#ff6b6b', '#ffc44d', '#3ecf8e'].map(c => <Box key={c} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c }} />)}
      </Box>
      <TemplatePreview template={template} scroll />
      <Box className="tpl-overlay" sx={{
        position: 'absolute', inset: 0, opacity: 0, transition: 'opacity .25s ease',
        background: 'linear-gradient(180deg, transparent 40%, rgba(20,17,43,0.75) 100%)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: 2.5,
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1, borderRadius: 999,
          background: BRAND.gradient, color: '#fff', fontWeight: 600, fontSize: 14,
        }}>
          View demo <ArrowForwardRounded sx={{ fontSize: 18 }} />
        </Box>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, px: 0.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{template.name}</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{template.category}</Typography>
    </Box>
  </Box>
);

// Category tabs with counts (plus a search toggle) over a grid of every
// template. Each card opens its demo.
const TemplateGallery = ({ limit }) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [showSearch, setShowSearch] = useState(false);

  const tabs = useMemo(() => [
    ['All', 'All Templates', TEMPLATES.length],
    ...CATEGORIES.map(c => [c, c, TEMPLATES.filter(t => t.category === c).length]).filter(([, , n]) => n > 0),
  ], []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATES.filter(t =>
      (category === 'All' || t.category === category) &&
      (!q || `${t.name} ${t.category} ${t.description}`.toLowerCase().includes(q))
    );
  }, [query, category]);

  const shown = limit ? results.slice(0, limit) : results;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box role="tablist" aria-label="Template categories" sx={{
          flex: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: { xs: 2.5, md: 4.5 }, rowGap: 1.5,
        }}>
          {tabs.map(([value, label, count]) => {
            const active = category === value;
            return (
              <Box key={value} component="button" type="button" role="tab" aria-selected={active}
                onClick={() => setCategory(value)}
                sx={{
                  border: 0, background: 'none', cursor: 'pointer', p: 0, pb: 0.5, font: 'inherit',
                  fontSize: { xs: 16, md: 20 }, fontWeight: 600, whiteSpace: 'nowrap',
                  color: active ? 'primary.main' : 'text.primary',
                  borderBottom: '2px solid', borderColor: active ? 'primary.main' : 'transparent',
                  transition: 'color .2s, border-color .2s', '&:hover': { color: 'primary.main' },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 4, borderRadius: 1 },
                }}>
                {label}
                <Box component="span" sx={{ fontWeight: 400, color: active ? 'primary.light' : 'text.disabled', ml: 0.5 }}>({count})</Box>
              </Box>
            );
          })}
        </Box>
        <IconButton onClick={() => setShowSearch(s => !s)} aria-label={showSearch ? 'Hide search' : 'Search templates'}
          aria-expanded={showSearch} sx={{ color: 'primary.main', mt: { xs: -0.5, md: -0.25 } }}>
          {showSearch ? <CloseRounded /> : <SearchRounded />}
        </IconButton>
      </Box>

      <Collapse in={showSearch} unmountOnExit>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <TextField
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoFocus
            placeholder="Search templates, e.g. bakery, shop, clinic"
            aria-label="Search templates"
            sx={{ width: { xs: '100%', sm: 440 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRounded sx={{ color: 'text.disabled' }} /></InputAdornment> } }}
          />
        </Box>
      </Collapse>

      {shown.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, mb: 1 }}>No results found</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>Check out popular templates below or try a different keyword.</Typography>
          <Button onClick={() => { setQuery(''); setCategory('All'); }}>Clear search</Button>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid', gap: { xs: 4, md: 5 }, mt: { xs: 4, md: 6 },
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        }}>
          {shown.map(t => <TemplateCard key={t.slug} template={t} />)}
        </Box>
      )}

      {limit && results.length > limit && (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button component={RouterLink} to="/templates" variant="contained" size="large" endIcon={<ArrowForwardRounded />} sx={{ px: 4, py: 1.5 }}>
            See all {TEMPLATES.length} templates
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TemplateGallery;
